// Web UI localization: English (US) and Bahasa Indonesia. The current locale
// is a tiny external store so non-React code (API client, formatters) can read
// it and components re-render when it changes. Questionnaire content written
// by creators is never translated.
import { detectLocale, localeTag, type Locale } from '@survey/core';
import { useCallback, useSyncExternalStore } from 'react';
import { en, type MessageKey } from './en';
import { id } from './id';

const KEY = 'survey.locale';
const catalogs: Record<Locale, Record<MessageKey, string>> = { en, id };

function initial(): Locale {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'en' || saved === 'id') return saved;
  } catch {
    // storage unavailable
  }
  return detectLocale(typeof navigator !== 'undefined' ? navigator.language : 'en');
}

let current: Locale = initial();
const listeners = new Set<() => void>();

function apply(l: Locale) {
  if (typeof document !== 'undefined') document.documentElement.lang = localeTag(l);
}
apply(current);

export function getLocale(): Locale {
  return current;
}

export function setLocale(l: Locale) {
  if (l === current) return;
  current = l;
  try {
    localStorage.setItem(KEY, l);
  } catch {
    // preference just won't persist
  }
  apply(l);
  listeners.forEach((fn) => fn());
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export type Vars = Record<string, string | number>;

/**
 * Translates a key. `{name}` placeholders are filled from vars. When vars has
 * a numeric `count`, `<key>_one` is used for 1 if it exists (English plurals).
 */
export function translate(locale: Locale, key: MessageKey, vars?: Vars): string {
  const cat = catalogs[locale];
  let template: string | undefined;
  if (vars && typeof vars.count === 'number' && vars.count === 1) {
    template = (cat as Record<string, string>)[`${key}_one`] ?? (en as Record<string, string>)[`${key}_one`];
  }
  template ??= cat[key] ?? en[key] ?? key;
  return vars ? template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : template;
}

/** Non-hook translator for code outside components (uses the current locale). */
export function t(key: MessageKey, vars?: Vars): string {
  return translate(current, key, vars);
}

export function useI18n() {
  const locale = useSyncExternalStore(subscribe, getLocale, getLocale);
  const tr = useCallback((key: MessageKey, vars?: Vars) => translate(locale, key, vars), [locale]);
  return { locale, setLocale, t: tr, tag: localeTag(locale) };
}

export type { MessageKey };
